import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  allocatedCases,
  holdCases,
  Allocation,
  updateCases,
} from "../../types/AllocationTypes";
import api from "../../utils/api";


interface AllocationState {
  allocatedCases: allocatedCases[];
  allocatedCase: allocatedCases | null;
  holdCases: holdCases[];
  updateCases: updateCases[];
  assignedCases: Allocation[];
  loading: boolean;
  success: boolean;
  error: string | null;
}

type UpdateByEmp = {
  empId: number;
  passkey?: never;
};

type UpdateByPasskey = {
  passkey: string;
  empId?: never;
};

type UpdateAllocatedCasesPayload = {
  updateCases: updateCases[];
  isAdmin: boolean;
} & (UpdateByEmp | UpdateByPasskey);

const initialState: AllocationState = {
  allocatedCases: [],
  allocatedCase: null,
  holdCases: [],
  updateCases: [],
  assignedCases: [],
  loading: false,
  success: false,
  error: null,
};

export const getAllAllocatedCases = createAsyncThunk<
  allocatedCases[],
  void,
  { rejectValue: string }
>("allocation/getAllAllocatedCases", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get(
      `/api/allocation/allocated/all-cases`
    );
    return res.data.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch allcated cases"
      );
    }
    return rejectWithValue("Failed to fetch allocated cases");
  }
});

export const getHoldCases = createAsyncThunk<
  holdCases[],
  void,
  { rejectValue: string }
>("allocation/getholdCases", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get(
      `/api/allocation/allocated/hold-cases`
    );
    return res.data.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch allcated cases"
      );
    }
    return rejectWithValue("Failed to fetch allocated cases");
  }
});


export const getAllocatedCase = createAsyncThunk<
  allocatedCases,
  { case_id: string },
  { rejectValue: string }
>(
  "allocation/getAllocatedCase",
  async ({ case_id }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/api/allocation/allocated/case`,
        { case_id }
      );
      return res.data.data;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to fetch allocated case"
        );
      }
      return rejectWithValue("Failed to fetch allocated case");
    }
  }
);

export const assignCasesToEmployee = createAsyncThunk<
  { success: boolean },
  { assignedCases: Allocation[] },
  { rejectValue: string }
>(
  "allocation/assignCasesToEmployee",
  async (payload: { assignedCases: Allocation[] }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/api/allocation/allocate/readycases`,
        payload
      );

      return response.data;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed To Allocate Cases"
        );
      }
      return rejectWithValue("Failed To Allocate Cases");
    }
  }
);

export const updateAllocatedCases = createAsyncThunk<
  { success: boolean; message: string },
  UpdateAllocatedCasesPayload,
  { rejectValue: string }
>("allocation/updateAllocatedCases", async (payload, { rejectWithValue }) => {
  try {
    const response = await api.post(
      `/api/allocation/allocate/update-cases`,
      payload
    );

    return response.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to Update cases"
      );
    }
    return rejectWithValue("Failed to update cases");
  }
});

const caseAllocationSlice = createSlice({
  name: "caseAllocation",
  initialState,
  reducers: {
    resetAllocationState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllAllocatedCases.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(
        getAllAllocatedCases.fulfilled,
        (state, action: PayloadAction<allocatedCases[]>) => {
          state.loading = false;
          state.allocatedCases = action.payload;
        }
      )
      .addCase(getAllAllocatedCases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching allocated cases";
      })

      .addCase(getAllocatedCase.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(
        getAllocatedCase.fulfilled,
        (state, action: PayloadAction<allocatedCases>) => {
          state.loading = false;
          state.allocatedCase = action.payload;
        }
      )
      .addCase(getAllocatedCase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching allocated cases";
      })

      .addCase(assignCasesToEmployee.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(assignCasesToEmployee.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(assignCasesToEmployee.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
      })

      .addCase(updateAllocatedCases.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(updateAllocatedCases.fulfilled, (state, action) => {
        state.loading = false;
        state.updateCases = action.meta.arg.updateCases;
        // Design-unit edits are handled by the caller; don't flip shared success
        // (avoids the "Allocation Successful" toast and selection wipe).
        const isDesignUnitOnly =
          action.meta.arg.updateCases.length > 0 &&
          action.meta.arg.updateCases.every((c) => c.action === "design_unit");
        const isJobCardUpdate =
          action.meta.arg.updateCases.length > 0 &&
          action.meta.arg.updateCases.every(
            (c) =>
              c.action === "Designed" ||
              (c.action === "QC2" && Boolean(c.job_card)),
          );
        state.success = !isDesignUnitOnly && !isJobCardUpdate;
      })
      .addCase(updateAllocatedCases.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        const isDesignUnitOnly =
          !!action.meta.arg?.updateCases?.length &&
          action.meta.arg.updateCases.every((c) => c.action === "design_unit");
        const isJobCardUpdate =
          !!action.meta.arg?.updateCases?.length &&
          action.meta.arg.updateCases.every(
            (c) =>
              c.action === "Designed" ||
              (c.action === "QC2" && Boolean(c.job_card)),
          );
        // Caller shows its own error toast for design-unit / job-card saves.
        state.error = isDesignUnitOnly || isJobCardUpdate
          ? null
          : (action.payload as string);
      })

      .addCase(getHoldCases.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(
        getHoldCases.fulfilled,
        (state, action: PayloadAction<holdCases[]>) => {
          state.loading = false;
          state.holdCases = action.payload;
        }
      )
      .addCase(getHoldCases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching allocated cases";
      });
  },
});

export const { resetAllocationState } = caseAllocationSlice.actions;
export default caseAllocationSlice.reducer;
